import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight } from 'lucide-react';

/* Geometry of the open wheel, in px and degrees.

   Everything is measured from the hub: a single point pinned to the left edge at the
   vertical middle of the viewport. A node is nothing but a rotation and a distance
   away from it, which is what lets the ring travel along the arc instead of cutting
   across it — see PINNED below. */
// Flush to the edge the wheel read as something clipped by the window; sat in a little
// way it reads as HUD laid over the page, and the hub is a whole circle.
const EDGE_INSET = 28;

const HUB_X = 20;
const RADIUS = 132;
// Where the nodes sit while the wheel is shut: folded back against the hub, close enough
// to read as one cluster rather than four loose dots.
const TUCKED_RADIUS = 19;
// Degrees above and below the horizontal. Short of a true 90 on purpose — a node at the
// pole sits over the hub's own column, and its label would start off the left edge.
const SPREAD = 74;

const HUB_SIZE = 34;
const NODE_SIZE = 11;
const RING_SIZE = 30;

// What the open wheel needs: the arc, plus the labels hanging off its outside.
const BOX_W = HUB_X + RADIUS + 168;
const BOX_H = 2 * RADIUS + 72;

// What stays hover-sensitive while shut. Every pixel of it is a pixel that can't be
// clicked through to a tile underneath, and at 1024–1250px wide the page's gutter is
// narrower than the strip, so it does overlap the first column there.
const CLOSED_W = 48;
const CLOSED_H = 128;

// How far across the screen the scrim reaches before it is fully transparent. Well past
// the widest label — the point isn't to cover the text but to give the fade room to be
// gradual. Past about half it stops looking like an overlay and starts looking like the
// gallery itself has dimmed.
const SCRIM_WIDTH = '43vw';

const toRadians = (degrees) => (degrees * Math.PI) / 180;

// First section at the top of the arc, last at the bottom, so the wheel reads in the
// same order as the page does.
const angleOf = (index, count) =>
    count < 2 ? 0 : SPREAD - (2 * SPREAD * index) / (count - 1);

// The track the nodes sit on, drawn once. Sweep flag 1 is clockwise in SVG's y-down
// space, which takes it from the top node down the right of the hub to the bottom one.
const ARC_PATH = (() => {
    const dx = RADIUS * Math.cos(toRadians(SPREAD));
    const dy = RADIUS * Math.sin(toRadians(SPREAD));
    const cy = BOX_H / 2;
    return `M ${HUB_X + dx} ${cy - dy} A ${RADIUS} ${RADIUS} 0 0 1 ${HUB_X + dx} ${cy + dy}`;
})();

/* Shared by everything hanging off the hub. The top-left origin is the whole trick: with
   it, a rotate() turns about the hub point rather than the element's own centre, so
   `rotate(θ) translateX(r)` places a node exactly on the arc and a transition on θ walks
   it around. Give these elements a size and the origin moves to their middle, and the
   ring starts swinging through the hub instead. */
const PINNED = { position: 'absolute', left: 0, top: 0, transformOrigin: '0 0' };

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/* One entry per page the wheel serves, since it is drawn over that page's own palette.
   `base` is the arc and the unlit nodes, `bright` the ring and anything lit, `node` the
   single dot the ring is currently on, which has to read against `bright`.

   Held as bare "r,g,b" so the alphas below can be mixed per part. The label classes are
   spelled out in full rather than built from a variable: Tailwind reads the source as
   text, and a class it never sees literally is a class it never generates. */
export const ACCENTS = {
    blue: {
        base: '59, 130, 246',
        bright: '147, 197, 253',
        node: '191, 219, 254',
        chevron: 'text-blue-400',
        labelOn: 'text-blue-100',
        labelOff: 'text-blue-300/70 group-hover:text-blue-200',
    },
    yellow: {
        base: '234, 179, 8',
        bright: '250, 204, 21',
        node: '254, 240, 138',
        chevron: 'text-yellow-400',
        labelOn: 'text-yellow-100',
        labelOff: 'text-yellow-300/70 group-hover:text-yellow-200',
    },
    // `base` and `bright` are the art portfolio's own pair rather than a stock red — the
    // red its landing is set in and the lighter one the header's links lift to on hover
    // there. Only `node` and the label classes come off the Tailwind ramp, which the
    // labels have to be spelled in anyway.
    red: {
        base: '225, 29, 46',
        bright: '242, 112, 122',
        node: '254, 202, 202',
        chevron: 'text-red-400',
        labelOn: 'text-red-100',
        labelOff: 'text-red-300/70 group-hover:text-red-200',
    },
};

/**
 * A weapon-wheel for the gallery: a hub on the left edge that fans its sections out
 * along a half circle on hover, with a ring you slide onto whichever one you want.
 *
 * The run it serves has no headings, so there is nothing on the page to anchor a
 * conventional section nav to and nothing to tell you where you are. The ring does both
 * jobs — it tracks the scroll position on its own, and follows the pointer while you are
 * choosing.
 *
 * Desktop only. The whole interaction is hover, and the open wheel is wider than a
 * phone's gutter.
 *
 * Portalled to <body> for the same reason CustomCursor is: the wheel is `fixed`, and
 * PageTransition's `.animate-fadeIn` ends on `transform: translateY(0)` with `forwards`.
 * A transformed ancestor becomes the containing block for its fixed descendants, so left
 * in the tree the wheel would be pinned to the middle of the whole scrolling page.
 *
 * @param sections     [{ name }] in page order — the arc is laid out top to bottom
 * @param activeIndex  section the page is currently scrolled into
 * @param visible      whether the gallery is in view; the wheel fades with it
 * @param onSelect     (index) => void, fired on click or Enter
 * @param accent       key into ACCENTS — the palette of the page it is drawn over
 */
export default function RadialSectionNav({
    sections,
    activeIndex = 0,
    visible = true,
    onSelect,
    accent = 'blue',
}) {
    const tone = ACCENTS[accent] ?? ACCENTS.blue;
    const [open, setOpen] = useState(false);
    // What the pointer or the keyboard focus is on, which is not necessarily where the
    // page is. Null puts the ring back to reporting the scroll position.
    const [pointedIndex, setPointedIndex] = useState(null);

    const count = sections.length;
    if (!count) return null;

    const ringIndex = pointedIndex ?? activeIndex;
    const radius = open ? RADIUS : TUCKED_RADIUS;

    const shut = () => {
        setOpen(false);
        setPointedIndex(null);
    };

    return createPortal(
        <>
            {/* The ground the labels are read against. A sibling of the wheel rather than
                a child because the wheel's own box is only as tall as the arc and is held
                off the left edge — this has to start at x=0 and run the whole viewport.

                scaleX rather than an animated width so it is composited rather than laid
                out, and because the gradient stays put under the scale: the fade lands
                where it is going to land instead of sweeping across. */}
            <div
                aria-hidden="true"
                className={`hidden lg:block fixed inset-y-0 left-0 z-[9997] pointer-events-none ${
                    visible ? '' : 'invisible'
                }`}
                style={{
                    width: SCRIM_WIDTH,
                    background:
                        'linear-gradient(to right, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.92) 26%, rgba(0,0,0,0.72) 52%, rgba(0,0,0,0.34) 76%, rgba(0,0,0,0) 100%)',
                    opacity: open && visible ? 1 : 0,
                    transform: open ? 'scaleX(1)' : 'scaleX(0.2)',
                    transformOrigin: '0 50%',
                    transition: `opacity 500ms ${EASE}, transform 600ms ${EASE}`,
                }}
            />

            <div
                className={`hidden lg:block fixed top-1/2 -translate-y-1/2 z-[9998] pointer-events-none transition-opacity duration-500 ${
                    visible ? 'opacity-100' : 'opacity-0 invisible'
                }`}
                style={{ left: EDGE_INSET, width: BOX_W, height: BOX_H }}
            >
            <nav
                aria-label="Gallery sections"
                className="absolute left-0 pointer-events-auto"
                /* Sized to the arc when open and to a strip when shut, with no
                   transition on the box itself: an animating hit area chases the
                   pointer out of its own bounds and the wheel flickers shut. */
                style={{
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: open ? BOX_W : CLOSED_W,
                    height: open ? BOX_H : CLOSED_H,
                }}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={shut}
                onFocusCapture={() => setOpen(true)}
                onBlurCapture={(e) => {
                    // Only when focus leaves the wheel entirely — moving between two
                    // nodes fires a blur too, and acting on that would shut it
                    // underneath a keyboard user mid-arrow.
                    if (!e.currentTarget.contains(e.relatedTarget)) shut();
                }}
            >
                {/* pointer-events-none is load-bearing: this is always drawn at the open
                    size, so while shut it hangs outside the hover strip and over the
                    gallery, and opacity 0 doesn't stop an element being hit. */}
                <svg
                    aria-hidden="true"
                    className={`absolute left-0 top-0 overflow-visible pointer-events-none transition-opacity duration-500 ${
                        open ? 'opacity-100' : 'opacity-0'
                    }`}
                    width={BOX_W}
                    height={BOX_H}
                    viewBox={`0 0 ${BOX_W} ${BOX_H}`}
                    /* The SVG is drawn against the open box, so while shut it has to be
                       held where it will be rather than recentred in the strip. */
                    style={{ top: '50%', marginTop: -BOX_H / 2 }}
                >
                    <path d={ARC_PATH} fill="none" stroke={`rgba(${tone.base}, 0.32)`} strokeWidth="1.5" strokeLinecap="round" />
                </svg>

                {/* Zero-size: it exists to be the origin every spoke rotates about. */}
                <div className="absolute" style={{ left: HUB_X, top: '50%' }}>
                    <div
                        aria-hidden="true"
                        className="absolute rounded-full border flex items-center justify-center transition-all duration-500"
                        style={{
                            width: HUB_SIZE,
                            height: HUB_SIZE,
                            marginLeft: -HUB_SIZE / 2,
                            marginTop: -HUB_SIZE / 2,
                            backgroundColor: 'rgba(0,0,0,0.85)',
                            borderColor: open ? `rgba(${tone.bright}, 0.9)` : `rgba(${tone.base}, 0.45)`,
                            transform: open ? 'scale(0.78)' : 'scale(1)',
                            transitionTimingFunction: EASE,
                        }}
                    >
                        <ChevronRight
                            className={`w-4 h-4 ${tone.chevron} transition-transform duration-500`}
                            style={{ transform: open ? 'rotate(180deg)' : 'none', transitionTimingFunction: EASE }}
                        />
                    </div>

                    {/* One ring for the whole wheel. Both transforms transition, so a
                        change of section swings it around the arc and an open/close
                        slides it in and out along the spoke. */}
                    <div
                        aria-hidden="true"
                        style={{
                            ...PINNED,
                            transform: `rotate(${-angleOf(ringIndex, count)}deg)`,
                            transition: `transform 450ms ${EASE}`,
                        }}
                    >
                        <div
                            style={{
                                ...PINNED,
                                transform: `translateX(${radius}px)`,
                                transition: `transform 500ms ${EASE}`,
                            }}
                        >
                            <span
                                className="block rounded-full border-2"
                                style={{
                                    width: RING_SIZE,
                                    height: RING_SIZE,
                                    marginLeft: -RING_SIZE / 2,
                                    marginTop: -RING_SIZE / 2,
                                    borderColor: `rgba(${tone.bright}, 0.95)`,
                                    boxShadow: `0 0 14px rgba(${tone.base}, 0.55)`,
                                    opacity: open ? 1 : 0.55,
                                    transition: `opacity 400ms ${EASE}`,
                                }}
                            />
                        </div>
                    </div>

                    {sections.map((section, index) => {
                        const angle = angleOf(index, count);
                        const isRinged = index === ringIndex;

                        return (
                            <div key={section.name} style={{ ...PINNED, transform: `rotate(${-angle}deg)` }}>
                                <div
                                    style={{
                                        ...PINNED,
                                        transform: `translateX(${radius}px)`,
                                        // Staggered so the nodes unfold in page order and
                                        // fold back the other way.
                                        transition: `transform 500ms ${EASE} ${
                                            open ? index * 45 : (count - 1 - index) * 30
                                        }ms`,
                                    }}
                                >
                                    {/* Back to horizontal, so the label reads straight
                                        whatever the spoke's angle. */}
                                    <div style={{ ...PINNED, transform: `rotate(${angle}deg)` }}>
                                        <button
                                            type="button"
                                            onClick={() => onSelect?.(index)}
                                            onMouseEnter={() => setPointedIndex(index)}
                                            onMouseLeave={() => setPointedIndex(null)}
                                            onFocus={() => setPointedIndex(index)}
                                            onBlur={() => setPointedIndex(null)}
                                            aria-current={index === activeIndex ? 'true' : undefined}
                                            className="absolute flex items-center gap-3 group focus:outline-none"
                                            /* The dot, not the button, is what sits on
                                               the arc — the label hangs off to its right. */
                                            style={{
                                                left: 0,
                                                top: 0,
                                                marginLeft: -NODE_SIZE / 2,
                                                transform: 'translateY(-50%)',
                                                pointerEvents: open ? 'auto' : 'none',
                                            }}
                                        >
                                            <span
                                                className="block rounded-full transition-all duration-300"
                                                style={{
                                                    width: NODE_SIZE,
                                                    height: NODE_SIZE,
                                                    backgroundColor: isRinged
                                                        ? `rgb(${tone.node})`
                                                        : `rgba(${tone.base}, 0.55)`,
                                                    boxShadow: isRinged ? `0 0 10px rgba(${tone.bright}, 0.8)` : 'none',
                                                }}
                                            />
                                            <span
                                                className={`whitespace-nowrap text-[0.7rem] uppercase tracking-[0.22em] transition-all duration-300 ${
                                                    open ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'
                                                } ${isRinged ? tone.labelOn : tone.labelOff}`}
                                                /* The outermost labels sit where the
                                                   scrim has already faded out, so they
                                                   carry their own contrast. */
                                                style={{ textShadow: '0 1px 7px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.9)' }}
                                            >
                                                {section.name}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </nav>
            </div>
        </>,
        document.body
    );
}
